'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { MicroPostLessonCheck } from './MicroPostLessonCheck';

/**
 * Lesson-page nav (prev / next) with an optional post-lesson
 * check gate. When {@code attachmentIds} is non-empty, the Next
 * button is hidden until the user completes or skips the checks.
 */
export interface LessonNavWithCheckProps {
  prevHref: string | null;
  nextHref: string | null;
  prevLabel?: string;
  nextLabel?: string;
  /** When present + non-empty, the Next button is gated behind the
   *  post-lesson check. */
  attachmentIds?: Array<string | number>;
}

export function LessonNavWithCheck({
  prevHref,
  nextHref,
  prevLabel,
  nextLabel,
  attachmentIds,
}: LessonNavWithCheckProps) {
  const { t } = useTranslation('site-builder-blocks');
  const prevText = prevLabel ?? t('microLessonNav.previous');
  const nextText = nextLabel ?? t('microLessonNav.next');
  const hasChecks = !!attachmentIds && attachmentIds.length > 0;
  const [unlocked, setUnlocked] = useState(!hasChecks);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {hasChecks && !unlocked && (
        <MicroPostLessonCheck
          attachmentIds={attachmentIds!}
          onAllComplete={() => setUnlocked(true)}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        {prevHref ? (
          <Link
            href={prevHref}
            style={{
              padding: 'var(--space-2-5, 10px) var(--space-5, 20px)',
              border: '1px solid var(--color-border, rgba(0,0,0,0.12))',
              borderRadius: 8,
              textDecoration: 'none',
              color: 'inherit',
              fontWeight: 500,
            }}
          >
            {prevText}
          </Link>
        ) : (
          <span />
        )}
        {nextHref && unlocked && (
          <Link
            href={nextHref}
            style={{
              padding: 'var(--space-2-5, 10px) var(--space-5, 20px)',
              background: 'var(--color-text-primary, #18181b)',
              color: 'var(--color-surface-card, #fff)',
              borderRadius: 8,
              textDecoration: 'none',
              fontWeight: 600,
            }}
          >
            {nextText}
          </Link>
        )}
      </div>
    </div>
  );
}
